<script lang="ts">
    import { onDestroy, createEventDispatcher } from 'svelte';
    import { get } from 'svelte/store';
    import { filters } from '$lib/filters';
    import { listVoters } from '$lib/api';
    import type { VoterSummary } from '$lib/stores/voters';
    import VoterDetail from '$components/VoterDetail.svelte';

    let items: VoterSummary[] = [];
    let total = 0;
    let loading = true;
    let error: string | null = null;
    let selectedVoter: VoterSummary | null = null;

    // The list renders as an overlay on the map page, which repaints the pin
    // for a voter changed here.
    const dispatch = createEventDispatcher<{ contactSaved: VoterSummary; voterUpdated: VoterSummary }>();

    async function load() {
        loading = true;
        error = null;
        try {
            const resp = await listVoters(get(filters), 200, 0);
            items = resp.items;
            total = resp.total;
        } catch (e: any) {
            error = e.message || 'Failed to load voters';
        } finally {
            loading = false;
        }
    }

    // Subscribe at component init time (not inside onMount) so it works in jsdom tests.
    // filters.subscribe fires immediately with current value, triggering the first load.
    const unsubFilters = filters.subscribe(() => { load(); });
    onDestroy(unsubFilters);

    function openDetail(v: VoterSummary) {
        selectedVoter = v;
    }

    function closeDetail() {
        selectedVoter = null;
    }

    function handleVoterUpdated(e: CustomEvent<VoterSummary>) {
        // Keep the row in step with an edit; the detail sheet stays open.
        const updated = e.detail;
        items = items.map(v => v.id === updated.id ? updated : v);
        dispatch('voterUpdated', updated);
    }

    function handleContactSaved(e: CustomEvent<VoterSummary>) {
        // Update the voter in the list with the updated data
        const updated = e.detail;
        items = items.map(v => v.id === updated.id ? updated : v);
        selectedVoter = null;
        dispatch('contactSaved', updated);
    }

    const PARTY_COLORS: Record<string, string> = {
        DEM: '#3b82f6',
        REP: '#ef4444',
        UNA: '#6b7280',
        LIB: '#f59e0b',
        GRE: '#22c55e',
    };

    function partyColor(p: string | null): string {
        return p && PARTY_COLORS[p] ? PARTY_COLORS[p] : '#9ca3af';
    }

    function lastContactSummary(v: VoterSummary): string {
        if (!v.latest_contact_at) return 'Never contacted';
        const d = new Date(v.latest_contact_at).toLocaleDateString();
        return `${v.latest_contact_result ?? 'contacted'} on ${d}`;
    }
</script>

<div class="list-page">
    <div class="list-header">
        {#if loading}
            <span class="count">Loading…</span>
        {:else if error}
            <span class="error">{error}</span>
        {:else}
            <span class="count">{total.toLocaleString()} voters</span>
        {/if}
    </div>

    <div class="list-body">
        {#if loading}
            <p class="center-msg">Loading…</p>
        {:else if error}
            <p class="center-msg error">{error}</p>
        {:else if items.length === 0}
            <p class="center-msg">No voters match the current filters.</p>
        {:else}
            {#each items as v (v.id)}
                <button class="voter-card" on:click={() => openDetail(v)}>
                    <div class="card-main">
                        <span class="voter-name">{v.first_name} {v.last_name}</span>
                        <span class="party-badge" style="background:{partyColor(v.party)}">{v.party ?? '—'}</span>
                    </div>
                    <div class="card-address">{v.address}{v.city ? `, ${v.city}` : ''}</div>
                    <div class="card-contact">{lastContactSummary(v)}</div>
                </button>
            {/each}
            {#if total > items.length}
                <p class="more-hint">Showing first {items.length} of {total.toLocaleString()}. Apply filters to narrow results.</p>
            {/if}
        {/if}
    </div>

    {#if selectedVoter}
        <VoterDetail
            voter={selectedVoter}
            on:close={closeDetail}
            on:contactSaved={handleContactSaved}
            on:voterUpdated={handleVoterUpdated}
        />
    {/if}
</div>

<style>
    .list-page { display: flex; flex-direction: column; height: 100%; background: #f9fafb; }
    .list-header { padding: 12px 16px; border-bottom: 1px solid #e5e7eb; background: white; }
    .count { font-size: .875rem; color: #6b7280; }
    .list-body { flex: 1; overflow-y: auto; padding: 8px 12px; display: flex; flex-direction: column; gap: 8px; }
    .voter-card {
        background: white; border: 1px solid #e5e7eb; border-radius: 10px;
        padding: 12px 14px; text-align: left; cursor: pointer; width: 100%;
        transition: box-shadow .15s;
    }
    .voter-card:hover { box-shadow: 0 2px 8px rgba(0,0,0,.1); }
    .card-main { display: flex; justify-content: space-between; align-items: center; }
    .voter-name { font-weight: 600; font-size: 1rem; }
    .party-badge {
        color: white; font-size: .75rem; padding: 2px 8px; border-radius: 10px;
        font-weight: 600; flex-shrink: 0;
    }
    .card-address { font-size: .875rem; color: #6b7280; margin-top: 2px; }
    .card-contact { font-size: .8rem; color: #9ca3af; margin-top: 4px; }
    .center-msg { text-align: center; padding: 32px; color: #6b7280; }
    .error { color: #dc2626; }
    .more-hint { text-align: center; font-size: .8rem; color: #9ca3af; padding: 8px; }
</style>
